// Волна 37, участок «Казармы» (боты «золото+»; гейты подчастей — у вызывающего
// кода: checkIfW36Feature(player, gameType, isPreW37…Player) из evaluation/goldPlus,
// ручки — configs/botW37Barracks): чистые функции перемещения с казарм.
//
//  - Рост за фазу снабжения на своей казарме и вне ее — по правилам редьюсера
//    (calcUnitUpdateBasedOnPendingSupply): рабочая казарма, рабочий лагерь рядом,
//    фракция (зверолюды 3/5, d4 1/2 и 0 вне казармы), прибавка лиги ботов
//    (botSupplySizeBonus), предел размера. Разница «остаться или уйти» — то, что
//    отряд теряет, уходя (B1 и B2).
//  - Резервный держатель (B1): без угрозы этого раунда отряд на своей рабочей
//    казарме, где он растет быстрее, чем вне ее, держит город так же, как с
//    квартала, если смежных казарме свободных кварталов города не меньше
//    W37_RESERVE_MIN_FREE_QUARTERS: захват — в конце раунда, а в следующем раунде
//    отряд встанет на смежный квартал одним шагом.
//  - Бонус держателя при угрозе только фантомного найма (B1 или T): ходящий
//    после бота нанимает отряд и в той же фазе ведет его на квартал — модели
//    волны 32 такой отряд не виден, и держателя на квартал ничто не ставило.
//
// Эльфы и крысолюды — вне участка, как и у прежнего бонуса роста на казарме:
// рост эльфов от казармы без лагеря не зависит, а казарма нужна им свободной под
// наймы; рой крысолюдов растет на фермах
import {calcUnitUpdateBasedOnPendingSupply} from "../../gameState/supply/calcUnitUpdateBasedOnPendingSupply";
import {getUnitMaxSizeForFaction} from "../../../configs/units";
import {FACTIONS} from "../../../constants/factions";
import {COMMON_BUILDING_TOKENS, SPECIAL_BUILDING_TOKENS} from "../../../constants/tokens";
import {getAllQuartersOfCity} from "../../gameState/getAllQuartersOfCity";
import {isUnitInsectoid} from "../../factions/insectoids/isUnitInsectoid";
import {BOT_WEIGHTS} from "../botConfig";
import {calcCityHolderBonus, calcCityHolderValue, CITY_THREAT_LEVELS} from "./cityCaptureThreat";
import {checkIfW37ThisRoundThreat} from "./w37Threat";

/** Фракции участка: эльфы и крысолюды держатся на казармах своими правилами */
export const checkIfW37BarracksGrowthFaction = (unit) => Boolean(unit)
  && unit.factionId !== FACTIONS.ELVES
  && unit.factionId !== FACTIONS.RATMEN;

/**
 * Рост отряда за одно базовое снабжение (по правилам редьюсера, без голода).
 * isOnWorkingBarracks — отряд на своей рабочей казарме; isCampAdjacent — рядом
 * рабочий военный лагерь (checkIfAdjacentToWorkingMilitaryCamp). Клетка вне
 * казармы — непустая (квартал): инсектоиды на пустой не растут вовсе
 */
export const calcW37SupplyGrowth = ({unit, owner, isOnWorkingBarracks, isCampAdjacent}) => {
  const maxSize = unit.maxSize || getUnitMaxSizeForFaction(unit.type, unit.factionId) || 0;
  const {sizeUpdate} = calcUnitUpdateBasedOnPendingSupply({...unit, maxSize}, owner, [{
    unitId: unit.id,
    isOnWorkingBarracks,
    isAdjacentToWorkingMilitaryCamp: isCampAdjacent,
    isOnEmptyHex: false,
  }]);
  return Math.max(0, sizeUpdate);
};

/**
 * Сколько размера отряд теряет за фазу снабжения, уходя со своей казармы на
 * квартал: рост на казарме минус рост вне ее (оба — с пределом размера).
 * 0 — на максимуме, у границы максимума или когда казарма не рабочая
 */
export const calcW37BarracksGrowthDelta = ({unit, owner, isOnWorkingBarracks, isCampAdjacent}) => (
  calcW37SupplyGrowth({unit, owner, isOnWorkingBarracks, isCampAdjacent})
  - calcW37SupplyGrowth({unit, owner, isOnWorkingBarracks: false, isCampAdjacent: false})
);

/**
 * Своя казарма → {isWorking — рабочая (checkIfOnWorkingBarracks), isCampWorking —
 * рядом рабочий лагерь (checkIfAdjacentToWorkingMilitaryCamp), isCampAdjacent —
 * рядом лагерь (как прежний бонус роста: без проверки, рабочий ли)}. Проверки —
 * функциями правил вызывающего кода: isWorkingBarracks(hexKey), isWorkingCampNear(hexKey)
 */
export const buildW37BarracksInfo = ({barracksHexKeys, specialBuildings, getNeighbors, isWorkingBarracks, isWorkingCampNear}) => {
  const info = new Map();
  (barracksHexKeys || []).forEach((hexKey) => {
    const isWorking = isWorkingBarracks(hexKey);
    info.set(hexKey, {
      isWorking,
      isCampWorking: isWorking && isWorkingCampNear(hexKey),
      isCampAdjacent: Boolean(specialBuildings) && getNeighbors(hexKey).some(
        (adjKey) => specialBuildings[adjKey] === SPECIAL_BUILDING_TOKENS.MILITARY_CAMP
      ),
    });
  });
  return info;
};

/**
 * Потеря роста за уход с клетки cellKey (своей казармы) для отряда unit: 0 —
 * не своя казарма, фракция вне участка, последний раунд (фазы снабжения после
 * войны нет) или отряд на этой клетке снабжения не получит. supplyCells —
 * досягаемость своих ферм с бюджетом (Set) или null: тогда снабжение есть везде
 * только при isSupplyEverywhere (ЭКОАНАРХИЗМ при своих фермах). Инсектоиды
 * базовое снабжение получают всегда
 */
export const calcW37GrowthDeltaAt = ({
  unit, owner, cellKey, barracksInfo, supplyCells, isSupplyEverywhere, isFinalRound,
}) => {
  const info = barracksInfo ? barracksInfo.get(cellKey) : undefined;
  if (!info || isFinalRound || !checkIfW37BarracksGrowthFaction(unit)) {
    return 0;
  }
  const isSupplied = isUnitInsectoid(unit) || (supplyCells ? supplyCells.has(cellKey) : Boolean(isSupplyEverywhere));
  if (!isSupplied) {
    return 0;
  }
  return calcW37BarracksGrowthDelta({
    unit, owner, isOnWorkingBarracks: info.isWorking, isCampAdjacent: info.isCampWorking,
  });
};

/**
 * Резервные держатели B1 — чистое ядро (без стора, для тестов).
 *  ownCities — свои города; cityThreatByCityKey — Map центральный квартал → запись
 *    getCityCaptureThreats({withRecruits: true}) (угроза этого раунда —
 *    checkIfW37ThisRoundThreat); units — все отряды; currentPlayerId;
 *  barracksInfo — buildW37BarracksInfo; getNeighbors(hexKey);
 *  minFreeQuarters — W37_RESERVE_MIN_FREE_QUARTERS;
 *  getGrowthDelta(unit, barracksHexKey) — потеря роста за уход (calcW37GrowthDeltaAt).
 * На каждой своей рабочей казарме со своим отрядом, который растет на ней
 * быстрее (потеря > 0), — по каждому ее городу без угрозы этого раунда:
 *  - свободных смежных казарме кварталов города не меньше minFreeQuarters —
 *    казарма резерв города (reserveCities): держатель G, волны 32 и оборона
 *    засчитываются отряду и на ней, а кварталы города — в blockedQuarterKeys;
 *  - иначе, если город держит свой отряд на квартале, — только blockedQuarterKeys:
 *    второй отряд на квартале ничего не добавляет, а рост теряет.
 * Возвращает Map казарма → {unitId, growthDelta, reserveCities: [{cityKey,
 * cityThreat, freeAdjacentQuarters}], blockedQuarterKeys: Set}; казармы без
 * резерва и без запрета в ответ не попадают
 */
export const collectW37BarracksStay = ({
  ownCities, cityThreatByCityKey, units, currentPlayerId, barracksInfo, getNeighbors, minFreeQuarters, getGrowthDelta,
}) => {
  const result = new Map();
  const occupiedCells = new Set();
  const ownUnitByHex = new Map();
  (units || []).forEach((unit) => {
    occupiedCells.add(unit.hexKey);
    if (unit.ownerId === currentPlayerId && !ownUnitByHex.has(unit.hexKey)) {
      ownUnitByHex.set(unit.hexKey, unit);
    }
  });
  const growthDeltaByUnitId = new Map();
  (ownCities || []).forEach((city) => {
    const cityThreat = cityThreatByCityKey.get(city.centralQuarter);
    if (!cityThreat || checkIfW37ThisRoundThreat(cityThreat)) {
      return;
    }
    const quarterSet = new Set(cityThreat.quarters || getAllQuartersOfCity(city));
    (city[COMMON_BUILDING_TOKENS.BARRACKS] || []).forEach((barracksHexKey) => {
      const unit = ownUnitByHex.get(barracksHexKey);
      const info = barracksInfo.get(barracksHexKey);
      if (!unit || !info || !info.isWorking) {
        return;
      }
      if (!growthDeltaByUnitId.has(unit.id)) {
        growthDeltaByUnitId.set(unit.id, getGrowthDelta(unit, barracksHexKey));
      }
      const growthDelta = growthDeltaByUnitId.get(unit.id);
      if (growthDelta <= 0) {
        return;
      }
      const freeAdjacentQuarters = getNeighbors(barracksHexKey)
        .filter((adjKey) => quarterSet.has(adjKey) && !occupiedCells.has(adjKey));
      const isReserve = freeAdjacentQuarters.length >= minFreeQuarters;
      if (!isReserve && cityThreat.holders.length === 0) {
        return;
      }
      const entry = result.get(barracksHexKey)
        || {unitId: unit.id, growthDelta, reserveCities: [], blockedQuarterKeys: new Set()};
      if (isReserve) {
        entry.reserveCities.push({cityKey: city.centralQuarter, cityThreat, freeAdjacentQuarters});
      }
      quarterSet.forEach((quarterHexKey) => entry.blockedQuarterKeys.add(quarterHexKey));
      result.set(barracksHexKey, entry);
    });
  });
  return result;
};

/**
 * Бонус держателя волны 32 резервному держателю на его казарме: как на
 * смежном свободном квартале города (calcCityHolderBonus) — наибольший по
 * городам резерва. Угрозы этого раунда у городов резерва нет, так что это
 * бонус NEXT_ROUND (0 при NONE). reserve — запись collectW37BarracksStay
 */
export const calcW37ReserveHolderBonus = (reserve, unit) => (reserve.reserveCities || []).reduce(
  (best, {cityThreat, freeAdjacentQuarters}) => Math.max(
    best, calcCityHolderBonus(cityThreat, unit, freeAdjacentQuarters[0])
  ),
  0
);

/** Казарма — резерв города cityKey для этого отряда (запись collectW37BarracksStay) */
export const checkIfW37ReserveForCity = (reserve, unitId, cityKey) => Boolean(reserve)
  && reserve.unitId === unitId
  && reserve.reserveCities.some((reserveCity) => reserveCity.cityKey === cityKey);

/**
 * Бонус держателя квартала cellKey города, которому в этом раунде угрожает только
 * фантомный найм ходящего после бота (isThisRoundByRecruit без уровня THIS_ROUND —
 * его бонус дает calcCityHolderBonus волны 32). cityThreat — запись
 * getCityCaptureThreats({withRecruits: true}); factor — доля ценности держателя
 * (W37_RECRUIT_HOLDER_FACTOR). Первый держатель (и «остаться» единственного) —
 * ценность города × factor; второй — только если держатели не переживут фантома
 * (isRecruitHolderAtRisk) и этот квартал фантом занял бы или вместе с ними
 * отряд переживет урон — доля подкрепления волны 32. 0 — бонуса нет
 */
export const calcW37RecruitHolderBonus = (cityThreat, unit, cellKey, factor) => {
  if (!cityThreat || cityThreat.isThisRoundByRecruit !== true || cityThreat.level === CITY_THREAT_LEVELS.THIS_ROUND) {
    return 0;
  }
  const cityValue = calcCityHolderValue(cityThreat.cityWinPoints) * factor;
  let otherHoldersCount = 0;
  let otherHoldersSize = 0;
  cityThreat.holders.forEach((holder) => {
    if (holder.id !== unit.id) {
      otherHoldersCount += 1;
      otherHoldersSize += holder.size;
    }
  });
  if (otherHoldersCount === 0) {
    return cityValue;
  }
  if (!cityThreat.isRecruitHolderAtRisk) {
    return 0;
  }
  const isReachedHere = Boolean(cityThreat.recruitReachedQuarters && cityThreat.recruitReachedQuarters.has(cellKey));
  const survivesDamage = otherHoldersSize + unit.size > (cityThreat.threatDamage || 0) + (cityThreat.recruitDamage || 0);
  return isReachedHere || survivesDamage ? cityValue * BOT_WEIGHTS.W32_CITY_HOLDER_REINFORCE_FACTOR : 0;
};
